import { ClassBuilder } from '@der/ui/components/calendar/types';
import { Task } from '@lit/task';
import { type CSSResult, html, type PropertyValues, TemplateResult } from 'lit';
import { classMap } from 'lit-html/directives/class-map.js';
import { property, state } from 'lit/decorators.js';
import { LocaleCode } from '~/common/interfaces';
import { RoundtripOfferViewModel } from '~/tailormade/interfaces';
import '~/ui/components/button';
import { ButtonVariant } from '~/ui/components/button/enum';
import { Calendar, CalendarTimeElementClass } from '~/ui/components/calendar';
import { SvgUtility } from '~/ui/utils';
import { withTranslations } from '~/ui/utils/mixins';
import { styles as calendarDefaultStyles } from './calendar-default.styles';
interface RoundtripOfferReponse {
    offers: Array<RoundtripOfferViewModel>;
    nextOffer: {
        startDate: string;
    } | null;
}

/**
 * Calendar component for displaying and selecting roundtrip offers.
 * Extends the base Calendar component with roundtrip-specific functionality
 * including price display, offer selection, and navigation between months.
 */
export class CalendarDefault extends withTranslations(Calendar) {
    static override styles: CSSResult[] = [
        ...((Array.isArray(super.styles) ? super.styles : [super.styles]) as CSSResult[]),
        calendarDefaultStyles,
    ];
    //TODO: do we need identifier here?
    @property({ type: String, attribute: 'identifier' }) identifier: string = '';
    @property({ type: Boolean, attribute: 'month-selection-dropdown' }) override monthSelectionDropdown: boolean =
        false;
    @property({ type: Number, attribute: 'month-dropdown-limit' }) override monthDropdownLimit = 12;

    @property({ type: String, attribute: 'analytics-name' }) analyticsName: string = '';
    @property({ type: String, attribute: 'data-url' }) dataUrl: string = '';
    @property({ type: String, attribute: 'phone-number' }) phoneNumber: string = '';
    @property({ type: String, attribute: 'roundtrip-key' }) roundtripKey: string = '';
    @property({ type: String, attribute: 'roundtrip-id' }) roundtripId: string = '';
    @property({ type: String, attribute: 'start-date' }) startDate: string = '';

    @state() private bestOffer?: RoundtripOfferViewModel;
    @state() private hasNextOffer: boolean = false;
    @state() private nextOfferStartDate: string = '';
    @state() private offers: RoundtripOfferViewModel[] = [];
    @state() private phoneNumberPattern: RegExp = /^[+]?[0-9\s\-()]{6,42}$/;
    @state() private phoneNumberIsValid: boolean = false;
    @state() private selectedMonth: string = new Date().toISOString().substring(0, 7);
    @state() private selectedDay: string = '';

    private _selectedOffer?: RoundtripOfferViewModel;
    private _currentRequestController?: AbortController;

    constructor() {
        super();
        this.dayRenderer = this.roundtripDayRenderer;
    }

    override connectedCallback(): void {
        super.connectedCallback();
        if (this.phoneNumber) {
            const isValid = this.phoneNumberPattern.test(this.phoneNumber);
            if (!isValid) {
                this.warn(this.tagName, {
                    message: 'Attribute: phone-number not valid',
                });
            }
            this.phoneNumberIsValid = isValid;
        }
    }

    override disconnectedCallback(): void {
        super.disconnectedCallback();
    }

    /**
     * Called when component properties are updated.
     * Updates the selected month and selected day based on the rendered calendar state.
     *
     * @param changed - Map of changed properties with their previous values
     */
    protected override updated(changed: PropertyValues): void {
        super.updated(changed);

        const firstTimeElement = this.renderRoot.querySelector('time.c-calendar__day');
        const visibleMonth = firstTimeElement?.getAttribute('datetime')?.substring(0, 7);
        if (visibleMonth && visibleMonth !== this.selectedMonth) {
            this.selectedMonth = visibleMonth;
        }

        const selectedTimeElement = this.renderRoot.querySelector('time.c-calendar__day[aria-selected="true"]');
        if (selectedTimeElement) {
            const selectedDate = selectedTimeElement.getAttribute('datetime');
            if (selectedDate && selectedDate !== this.selectedDay) {
                if (this.hasPriceForIsoDate(selectedDate)) {
                    this.selectedDay = selectedDate;
                }
            }
        }
    }

    /**
     * Checks if there is a price/offer available for the given ISO date string.
     *
     * @param dateIso - ISO date string in format YYYY-MM-DD
     * @returns True if an offer exists for the given date, false otherwise
     */
    private hasPriceForIsoDate(dateIso: string): boolean {
        if (!dateIso) return false;
        return this.offers.some((d) => d.startDate.substring(0, 10) === dateIso);
    }

    /**
     * Formats an ISO date string into a localized date string.
     * Special handling for French locale (fr-CH) to use "1er" for the first day of the month.
     *
     * @param isoDate - ISO date string to format
     * @returns Formatted date string in the component's locale, or empty string if date is invalid
     */
    private formatNextOfferDate(isoDate: string): string {
        if (!isoDate) return '';

        const date = new Date(isoDate);

        const formatter = new Intl.DateTimeFormat(this.lang, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });

        // If not French, just return the default localized format
        if (this.lang !== 'fr-CH') {
            return formatter.format(date);
        }

        // Special French rule for "1er"
        const parts = formatter.formatToParts(date);
        const day = parts.find((p) => p.type === 'day')?.value || '';
        const month = parts.find((p) => p.type === 'month')?.value || '';
        const year = parts.find((p) => p.type === 'year')?.value || '';

        const formattedDay = day === '1' ? '1er' : day;
        return `le ${formattedDay} ${month} ${year}`;
    }

    /**
     * Determines if the action button should be enabled.
     * Button is enabled if either:
     * - A day is selected with a valid price and offers are loaded, or
     * - There is a next offer available to navigate to.
     *
     * @returns True if the button should be enabled, false otherwise
     */
    private get isButtonEnabled(): boolean {
        const hasSelectedDay = !!this.selectedDay;
        const hasPrice = this.selectedDay ? this.hasPriceForIsoDate(this.selectedDay) : false;
        const hasPriceData = this.offers.length > 0;
        const hasNextOffer = this.hasNextOffer;

        return (hasSelectedDay && hasPrice && hasPriceData) || hasNextOffer;
    }

    /**
     * Navigates the calendar to display the specified month.
     * Updates the active month, year, and selected month state.
     *
     * @param dateString - Date string in ISO format (YYYY-MM-DD or YYYY-MM)
     */
    private navigateToMonth(dateString: string): void {
        if (!dateString) return;

        const date = new Date(dateString);
        const month = date.getMonth() + 1;
        const year = date.getFullYear();

        this.activeMonth = month;
        this.activeYear = year;

        this.selectedMonth = dateString.substring(0, 7);

        this.requestUpdate();
    }

    /**
     * Handles the click event on the action button.
     * If there is a next offer available, navigates to that month.
     * Otherwise, requests the selected offer.
     */
    private handleButtonClick(): void {
        if (this.hasNextOffer && this.nextOfferStartDate) {
            this.navigateToMonth(this.nextOfferStartDate);
        } else if (this.selectedDay) {
            this.requestOffer();
        }
    }

    /**
     * This task:
     * - Cancels any previous pending request via `AbortController`
     * - Builds the correct API URL with all required query parameters
     * - Fetches offer data for the selected month
     * - Updates component properties (`offers`, `bestOffer`, etc.) only if the request was not aborted
     *
     * @type {Task<[string, LocaleCode, string, string, string, string, string, string], void>}
     *
     * @property {AbortController | null} _currentRequestController - Controller used to abort in-flight requests when new ones start.
     * @property {string} dataUrl - The base URL for fetching roundtrip offers.
     * @property {Array<RoundtripOffer>} offers - List of currently loaded offers.
     * @property {RoundtripOffer | null} bestOffer - The offer with the lowest price.
     * @property {boolean} hasNextOffer - Whether there is another offer available in the API response.
     * @property {string} nextOfferStartDate - The start date of the next available offer, if present.
     *
     */
    private _contentTask: Task = new Task(this, {
        task: async ([identifier, lang, brand, tenant, currency, roundtripId, roundtripKey, selectedMonth]: [
            string,
            LocaleCode,
            string,
            string,
            string,
            string,
            string,
            string,
        ]) => {
            // Cancel any previous request
            if (this._currentRequestController) {
                this._currentRequestController.abort();
            }

            // Create new abort controller for this request
            this._currentRequestController = new AbortController();

            this.offers = [];

            const selectedDate = new Date(selectedMonth + '-01');
            const today = new Date();
            const currentMonth = today.toISOString().substring(0, 7);

            // For current month: start from today, for other months: start from beginning of month
            const startDate =
                selectedMonth === currentMonth
                    ? today.toISOString().substring(0, 10)
                    : selectedDate.toISOString().substring(0, 10);

            const endOfMonth = new Date(selectedDate);
            endOfMonth.setMonth(endOfMonth.getMonth() + 1, 0);
            const endDate = endOfMonth.toISOString().substring(0, 10);

            const url = new URL(this.dataUrl);
            if (identifier) url.searchParams.set('identifier', identifier);
            if (lang) url.searchParams.set('lang', lang);
            if (roundtripId) url.searchParams.set('roundtripId', roundtripId);
            if (roundtripKey) url.searchParams.set('roundtripKey', roundtripKey);
            if (brand) url.searchParams.set('brand', brand);
            if (tenant) url.searchParams.set('tenant', tenant);
            if (currency) url.searchParams.set('currency', currency);

            url.searchParams.set('startDate', startDate);
            url.searchParams.set('endDate', endDate);

            const response = await fetch(url.toString(), {
                signal: this._currentRequestController.signal,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch offers: ${response.statusText}`);
            }

            const responseData = (await response.json()) as RoundtripOfferReponse;

            // Only update state if this request is still the current one
            if (!this._currentRequestController.signal.aborted) {
                this.offers = responseData.offers;
                this.bestOffer = this.offers.reduce(
                    (bestOffer, curr) => (!bestOffer || bestOffer.price.amount < curr.price.amount ? curr : bestOffer),
                    this.offers[0]
                );
                this.hasNextOffer = responseData.nextOffer !== null;
                this.nextOfferStartDate = responseData.nextOffer?.startDate || '';
                this.requestUpdate();
            }
        },
        args: () =>
            [
                this.identifier,
                this.lang,
                this.brand,
                this.tenant,
                this.currency,
                this.roundtripId,
                this.roundtripKey,
                this.selectedMonth,
            ] as [string, LocaleCode, string, string, string, string, string, string],
    });

    /**
     * Finds the roundtrip offer for a specific day in the currently selected month.
     *
     * @param day - Day of the month (1-31)
     * @returns The roundtrip offer for the given day, or undefined if no offer exists
     */
    private findPriceForDate(day: number): RoundtripOfferViewModel | undefined {
        const baseYearMonth =
            this.selectedMonth ||
            (this.startDate
                ? this.startDate.substring(0, 7)
                : this.offers.length > 0
                  ? this.offers[0]?.startDate.substring(0, 7)
                  : new Date().toISOString().substring(0, 7));

        const formattedDate = `${baseYearMonth}-${String(day).padStart(2, '0')}`; // YYYY-MM-DD

        return this.offers.find((data) => data.startDate.substring(0, 10) === formattedDate);
    }

    /**
     * Dispatches analytics event and custom 'der.selected' event for the selected offer.
     * Called when the user confirms their selection by clicking the action button.
     */
    private requestOffer(): void {
        // Dispatch analytics event first
        this._dispatchAnalyticsEvent();

        // Dispatch a custom event that parent components can listen to
        this.dispatchEvent(
            new CustomEvent('der.selected', {
                detail: this._selectedOffer,
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Dispatches an analytics event with details about the selected offer.
     * The event includes offer URI, roundtrip key, analytics name, and selected date.
     */
    private _dispatchAnalyticsEvent(): void {
        const analyticsData = {
            offerUri: this._selectedOffer?.uri || '',
            roundtripKey: this.roundtripKey,
            name: this.analyticsName || '',
            selectedDate: this.selectedDay || '',
        };

        this.dispatchEvent(
            new CustomEvent('der-analytics', {
                detail: {
                    name: 'form-show',
                    element_name: this.tagName.toLowerCase(),
                    element_id: this.id ?? '',
                    action: 'next step clicked',
                    category: 'calendar',
                    data: analyticsData,
                },
                bubbles: true,
                composed: true,
            })
        );
    }

    /**
     * Handles the click event on a calendar day.
     * Updates the selected offer and selected day state.
     *
     * @param day - Day of the month that was clicked
     * @param offer - The roundtrip offer associated with this day, if available
     */
    private handleRoundtripDayClick(day: number, offer: RoundtripOfferViewModel | undefined): void {
        if (!offer) return;
        this._selectedOffer = offer;
        const dateString = offer.startDate.slice(0, 10);
        this.selectedDay = dateString;
        this.requestUpdate();
    }

    /**
     * Handles keyboard events on calendar days for accessibility.
     * Triggers day selection on Enter or Space key press.
     *
     * @param event - The keyboard event
     * @param day - Day of the month
     * @param offer - The roundtrip offer associated with this day, if available
     */
    private handleRoundtripDayKeydown(
        event: KeyboardEvent,
        day: number,
        offer: RoundtripOfferViewModel | undefined
    ): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.handleRoundtripDayClick(day, offer);
        }
    }

    /**
     * Determines whether to show the "best price" indicator text.
     * Returns true if there are visible offers with different prices.
     *
     * @returns True if the best price text should be displayed, false otherwise
     */
    private showBestPriceText(): boolean {
        let visibleOffers: RoundtripOfferViewModel[];
        if (this.bookingDayOffset) {
            visibleOffers = this.offers.slice(this.bookingDayOffset);
            return visibleOffers.some((o) => o.price.bestPrice);
        } else if (this.offers) {
            return this.offers.some((o) => o.price.bestPrice);
        }
        return false;
    }

    /**
     * Returns custom CSS class builders for calendar day elements.
     * Adds unselectable class to days that don't have a price/offer available.
     *
     * @param day - Day of the month, or null if not a valid day
     * @param month - Month number (1-12)
     * @param year - Year number
     * @returns Array of class builders to apply to the time element
     */
    override getCustomTimeElementClassBuilders(day: number | null, month: number, year: number): ClassBuilder[] {
        const offer = day ? this.findPriceForDate(day) : undefined;
        const dateIsoString = `${this.selectedMonth}-${String(day).padStart(2, '0')}`;
        const hasPrice = day && offer ? this.hasPriceForIsoDate(dateIsoString) : false;
        const isUnselectableDueToNoPrice = !!day && !hasPrice;

        return [
            ...super.getCustomTimeElementClassBuilders(day, month, year),
            { class: [CalendarTimeElementClass.Unselectable], case: isUnselectableDueToNoPrice },
        ];
    }

    /**
     * Custom renderer for calendar day elements.
     * Renders each day with its number, price, and appropriate styling based on selection state and offer availability.
     *
     * @param day - Day of the month to render, or null if not a valid day
     * @param isUnselectable - Whether the day is unselectable due to calendar constraints
     * @returns Template result for the day element
     */
    roundtripDayRenderer(day: number | null, isUnselectable: boolean): TemplateResult {
        const isValidForEarliestBookingDate =
            day && this.isValidForEarliestBookingDate(day, this.activeMonth, this.activeYear);
        const dateIsoString = `${this.selectedMonth}-${String(day).padStart(2, '0')}`;
        const offer = day && isValidForEarliestBookingDate ? this.findPriceForDate(day) : undefined;
        const hasPrice = day && offer ? this.hasPriceForIsoDate(dateIsoString) : false;
        const isUnselectableDueToNoPrice = day && !hasPrice;
        const isSelected = day && this.selectedDay === dateIsoString;

        return html` <span
            class=${classMap({
                'm-calendar-default__day': true,
                'm-calendar-default__day--selected': !!isSelected,
                'm-calendar-default__day--best-date': offer?.price.bestPrice ?? false,
            })}
            tabindex="${isUnselectable || isUnselectableDueToNoPrice ? '-1' : 0}"
            style="${isUnselectableDueToNoPrice ? 'pointer-events: none;' : ''}"
            @click="${day && hasPrice ? () => this.handleRoundtripDayClick(day, offer) : undefined}"
            @keydown="${day ? (event: KeyboardEvent) => this.handleRoundtripDayKeydown(event, day, offer) : undefined}"
            role="button"
            aria-label="${day ? `Select ${day} ${this.selectedMonth}` : ''}"
        >
            <span class="m-calendar-default__day-number">${day}</span>
            <span class="m-calendar-default__day-price"> ${offer ? offer.price.formattedString : '-'} </span>
        </span>`;
    }

    /**
     * Renders the calendar component with header, calendar grid, and action button.
     * Includes best price indicator, button with dynamic text based on state, and optional contact information.
     *
     * @returns Template result for the complete calendar component
     */
    override render() {
        return html`<div
            class=${classMap({
                'm-calendar-default': true,
                'm-calendar-default--disabled': !!this.hasNextOffer,
            })}
        >
            <p class="m-calendar-default__header-text">${this.trans('calendarTitle')}</p>
            ${this._contentTask.render({
                pending: () => super.render(),
                error: () => super.render(),
                complete: () => super.render(),
            })}
            <div class="m-calendar-default__button-text-wrapper">
                <div class="m-calendar-default__best-price">
                    <span class="m-calendar-default__best-price-text ${this.showBestPriceText() ? '' : 'hidden'}"
                        >${this.trans('calendarBestPrice')}</span
                    >
                </div>
                <der-ui-button
                    variant=${this.hasNextOffer ? ButtonVariant.Info : ButtonVariant.Primary}
                    .disabled=${!this.isButtonEnabled}
                    @click=${this.handleButtonClick}
                    .wrapText=${true}
                    subtitle=${this.hasNextOffer
                        ? this.formatNextOfferDate(this.nextOfferStartDate)
                        : this.trans('requestOfferButtonSubLabel', {
                              currency: this.bestOffer?.price.currency || '',
                          })}
                >
                    ${this.hasNextOffer
                        ? this.trans('nextOfferDateButtonLabel')
                        : this.trans('requestOfferButtonLabel')}
                </der-ui-button>
                ${!this.phoneNumberIsValid
                    ? ''
                    : html` <div class="m-calendar-default__contact">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40" fill="none">
                              ${SvgUtility.systemPhone}
                          </svg>
                          <span class="m-calendar-default__contact-text">
                              ${this.transRichText('calendarInfoText', { phoneNumber: this.phoneNumber })}
                          </span>
                      </div>`}
            </div>
        </div>`;
    }
}
